import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { QuizAttemptSubmitResponse, QuizPlayResponse, QuizSurveyQuestion, QuizTimerMode } from '../types';
import { CheckCircle2, Clock, Lock } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Layout } from '../components/layout/Layout';
import { QuizExitDialog } from '../components/quiz/QuizExitDialog';
import { QuizQuestionMediaView } from '../components/quiz/QuizQuestionMediaView';
import { cn } from '../lib/utils';
import { shouldShowCampaignSponsorBlock } from '../lib/sponsor';
import { CampaignSponsorBlock } from '../components/quiz/CampaignSponsorBlock';
import { CampaignSponsorLogo } from '../components/quiz/CampaignSponsorLogo';
import { campaignHeaderSubtitle } from '../lib/campaign-header';
import { formatDateTime } from '../lib/format-datetime';
import { MarkdownContent } from '../components/markdown/MarkdownContent';
import { useApiJson } from '../hooks/useApi';
import { useQuizExitGuard } from '../hooks/useQuizExitGuard';
import { apiFetch } from '../lib/api';
import {
  clearQuizPlayDraft,
  computeSecondsLeft,
  createOptionOrders,
  createShuffledOrder,
  isValidDisplayOrder,
  isValidOptionOrders,
  loadQuizPlayDraft,
  normalizeQuizAnswers,
  saveQuizPlayDraft,
} from '../lib/quiz-play-storage';
import { submitQuizAttemptKeepalive } from '../lib/quiz-submit';

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${`${s}`.padStart(2, '0')}`;
}

export function QuizPage() {
  const { data: playData, loading, mutate } = useApiJson<QuizPlayResponse>('/api/quiz-campaigns/play');

  const [isQuizActive, setIsQuizActive] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [displayOrder, setDisplayOrder] = useState<number[]>([]);
  const [optionOrders, setOptionOrders] = useState<number[][]>([]);
  const [quizFinished, setQuizFinished] = useState(false);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);
  const [isSubmittingAttempt, setIsSubmittingAttempt] = useState(false);
  const [surveyQuestion, setSurveyQuestion] = useState<QuizSurveyQuestion | null>(null);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const hasSubmittedRef = useRef(false);

  const play = playData?.ok ? playData : null;
  const questions = play?.questions ?? [];
  // displayOrder[i] is the original (scoring) index of the question shown at display position i.
  // Answers stay keyed to the original order so scoring/saving is unaffected by shuffled display.
  const displayIndex = displayOrder[currentQuestionIndex] ?? currentQuestionIndex;
  // currentOptionOrder[j] is the original option index shown at display position j for the current question.
  const currentOptionOrder =
    optionOrders[displayIndex] ??
    Array.from({ length: questions[displayIndex]?.options.length ?? 0 }, (_, i) => i);
  const timerSeconds = play?.timerSeconds ?? 0;
  const timerMode: QuizTimerMode = play?.timerMode ?? 'per_round';
  const unavailable = play?.state === 'unavailable';
  const shouldBlockExit = isQuizActive && play?.state === 'ready';
  const [questionStartedAt, setQuestionStartedAt] = useState<string[]>([]);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const shouldShowSurvey = Boolean(surveyQuestion) && !surveySubmitted;

  const submitQuiz = useCallback(async () => {
    if (!play?.session || !startedAt || hasSubmittedRef.current) return;
    setIsSubmittingAttempt(true);
    setSubmitError(null);
    const payload = {
      answers: normalizeQuizAnswers(answers, questions.length),
      startedAt,
      completedAt,
      questionStartedAt,
    };
    try {
      const response = await apiFetch<QuizAttemptSubmitResponse>(`/api/quiz-sessions/${encodeURIComponent(play.session.id)}/attempts`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      hasSubmittedRef.current = true;
      clearQuizPlayDraft();
      setIsQuizActive(false);
      setSurveyQuestion(response.surveyQuestion);
      setSurveySubmitted(response.surveySubmitted);
      await mutate();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit quiz');
    } finally {
      setIsSubmittingAttempt(false);
    }
  }, [play?.session, startedAt, completedAt, questionStartedAt, answers, questions.length, mutate]);

  const submitSurvey = useCallback(async (selectedOption: number) => {
    if (!play?.session || !surveyQuestion || surveySubmitted) return;
    setSurveyError(null);
    try {
      await apiFetch(`/api/quiz-sessions/${encodeURIComponent(play.session.id)}/survey-response`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: surveyQuestion.id,
          selectedOption,
        }),
      });
      setSurveySubmitted(true);
      await mutate();
    } catch (e) {
      setSurveyError(e instanceof Error ? e.message : 'Failed to submit survey');
    }
  }, [play?.session, surveyQuestion, surveySubmitted, mutate]);

  const { showDialog, stayOnQuiz, leaveQuiz } = useQuizExitGuard({
    when: shouldBlockExit,
    onConfirmLeave: async () => {
      setIsSubmittingLeave(true);
      try {
        await submitQuiz();
      } finally {
        setIsSubmittingLeave(false);
      }
    },
  });

  useEffect(() => {
    if (loading || hasRestoredDraft || !play?.session || play.state !== 'ready') return;

    const draft = loadQuizPlayDraft();
    if (!draft || draft.sessionId !== play.session.id) {
      if (draft) clearQuizPlayDraft();
      setHasRestoredDraft(true);
      return;
    }

    const activeQuestionStartedAt =
      draft.timerMode === 'per_question'
        ? draft.questionStartedAt[Math.min(draft.currentQuestionIndex, draft.questionStartedAt.length - 1)] ?? draft.startedAt
        : draft.startedAt;
    const remaining = computeSecondsLeft(activeQuestionStartedAt, draft.timerSeconds);
    setIsQuizActive(true);
    setCurrentQuestionIndex(draft.currentQuestionIndex);
    setAnswers(draft.answers);
    setDisplayOrder(
      isValidDisplayOrder(draft.displayOrder, play.questions.length)
        ? draft.displayOrder
        : createShuffledOrder(play.questions.length),
    );
    setOptionOrders(
      isValidOptionOrders(draft.optionOrders, play.questions)
        ? draft.optionOrders
        : createOptionOrders(play.questions),
    );
    setQuizFinished(draft.quizFinished || (draft.timerMode === 'per_round' && remaining <= 0));
    setStartedAt(draft.startedAt);
    setQuestionStartedAt(draft.questionStartedAt);
    setCompletedAt(draft.completedAt);
    setSecondsLeft(remaining);
    setSubmitError(null);
    setHasRestoredDraft(true);
  }, [loading, hasRestoredDraft, play]);

  useEffect(() => {
    if (!isQuizActive || !play?.session || !play.campaign || !startedAt) return;

    saveQuizPlayDraft({
      sessionId: play.session.id,
      campaignId: play.campaign.id,
      currentQuestionIndex,
      answers,
      startedAt,
      quizFinished,
      timerSeconds,
      timerMode,
      questionStartedAt,
      completedAt,
      displayOrder,
      optionOrders,
    });
  }, [isQuizActive, play, startedAt, currentQuestionIndex, answers, quizFinished, timerSeconds, timerMode, questionStartedAt, completedAt, displayOrder, optionOrders]);

  useEffect(() => {
    if (play?.state === 'completed') {
      clearQuizPlayDraft();
      hasSubmittedRef.current = true;
    }
  }, [play?.state]);

  useEffect(() => {
    setSurveyQuestion(play?.surveyQuestion ?? null);
    setSurveySubmitted(play?.surveySubmitted ?? false);
  }, [play?.surveyQuestion, play?.surveySubmitted]);

  useEffect(() => {
    if (!shouldBlockExit || !play?.session || !startedAt) return;

    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted || hasSubmittedRef.current) return;
      submitQuizAttemptKeepalive(
        play.session!.id,
        answers,
        startedAt,
        questions.length,
        completedAt,
        questionStartedAt,
      );
      clearQuizPlayDraft();
      hasSubmittedRef.current = true;
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [shouldBlockExit, play?.session, startedAt, answers, questions.length, completedAt, questionStartedAt]);

  useEffect(() => {
    if (!isQuizActive || secondsLeft === null) return;
    if (secondsLeft <= 0) {
      if (timerMode === 'per_round') {
        setCompletedAt(new Date().toISOString());
        setQuizFinished(true);
      }
      return;
    }
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev === null ? prev : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [isQuizActive, secondsLeft, timerMode]);

  const startQuiz = () => {
    hasSubmittedRef.current = false;
    setIsQuizActive(true);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setDisplayOrder(createShuffledOrder(questions.length));
    setOptionOrders(createOptionOrders(questions));
    setQuizFinished(false);
    setSubmitError(null);
    const now = new Date().toISOString();
    setStartedAt(now);
    setQuestionStartedAt([now]);
    setCompletedAt(null);
    setSecondsLeft(timerSeconds);
    setSurveyQuestion(null);
    setSurveySubmitted(false);
    setSurveyError(null);
  };

  const advanceQuestion = useCallback((nextAnswers?: number[]) => {
    if (currentQuestionIndex < questions.length - 1) {
      window.setTimeout(() => {
        const nextStartedAt = new Date().toISOString();
        setCurrentQuestionIndex((prev) => prev + 1);
        if (timerMode === 'per_question') {
          setQuestionStartedAt((prev) => [...prev, nextStartedAt]);
          setSecondsLeft(timerSeconds);
        }
        if (nextAnswers) setAnswers(nextAnswers);
      }, 300);
      return;
    }

    setCompletedAt(new Date().toISOString());
    setQuizFinished(true);
  }, [currentQuestionIndex, questions.length, timerMode, timerSeconds]);

  const handleAnswerSelect = (optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[displayIndex] = optionIndex;
    setAnswers(newAnswers);
    advanceQuestion(newAnswers);
  };

  useEffect(() => {
    if (!isQuizActive || secondsLeft === null || timerMode !== 'per_question' || quizFinished) return;
    if (secondsLeft === 0) {
      advanceQuestion();
    }
  }, [advanceQuestion, isQuizActive, quizFinished, secondsLeft, timerMode]);

  useEffect(() => {
    if (!quizFinished || isQuizActive === false || hasSubmittedRef.current || isSubmittingAttempt || submitError) return;
    void submitQuiz();
  }, [quizFinished, isQuizActive, isSubmittingAttempt, submitError, submitQuiz]);

  return (
    <Layout>
      <AnimatePresence>
        {showDialog && (
          <QuizExitDialog
            onStay={stayOnQuiz}
            onLeave={() => void leaveQuiz()}
            isSubmitting={isSubmittingLeave}
          />
        )}
      </AnimatePresence>
      <div className="max-w-2xl mx-auto space-y-8">
        <header className="text-center space-y-4">
          <CampaignSponsorLogo
            sponsorLogoUrl={play?.campaign.sponsorLogoUrl}
            sponsorNames={play?.campaign.sponsorNames}
            size="prominent"
          />
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {play?.campaign.title ?? 'Quiz'}
          </h1>
          <MarkdownContent
            text={campaignHeaderSubtitle(
              play?.campaign.description,
              play?.campaign.title,
              'Answer all questions before the timer runs out.',
            )}
            className="text-[var(--color-text-secondary)] text-sm mt-1"
          />
          {shouldShowCampaignSponsorBlock(
            play?.campaign.sponsorNames ?? [],
            play?.campaign.sponsorLinkUrl,
          ) && (
            <CampaignSponsorBlock
              sponsorNames={play?.campaign.sponsorNames ?? []}
              sponsorLinkUrl={play?.campaign.sponsorLinkUrl}
            />
          )}
        </header>

        <div className="bg-[var(--color-surface-1)] p-6 md:p-8 rounded-[var(--radius-xl)] border border-[var(--color-border)]">
          <div className="max-w-2xl mx-auto text-center">
            {loading && (
              <p className="text-[var(--color-text-secondary)] text-sm">Loading quiz…</p>
            )}

            {!loading && (unavailable || !play) && (
              <>
                <Lock className="w-10 h-10 mx-auto mb-4 text-[var(--color-text-tertiary)]" />
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-4">
                  No Quiz Available
                </h3>
                <p className="text-[var(--color-text-secondary)] text-sm">
                  There is no active quiz campaign right now. Check back when the next campaign opens.
                </p>
              </>
            )}

            {!loading && play?.state === 'waiting' && (
              <>
                <Clock className="w-10 h-10 mx-auto mb-4 text-[var(--color-accent)]" />
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-4">
                  Next Round Coming Up
                </h3>
                {play.nextSession ? (
                  <p className="text-[var(--color-text-secondary)] text-sm">
                    <span className="font-medium text-[var(--color-text-primary)]">{play.nextSession.label}</span>
                    {' '}opens {formatDateTime(play.nextSession.opensAt)}.
                  </p>
                ) : (
                  <p className="text-[var(--color-text-secondary)] text-sm">
                    No rounds are scheduled yet for this campaign.
                  </p>
                )}
              </>
            )}

            {!loading && play?.state === 'ready' && !isQuizActive && (
              <>
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">
                  {play.session?.label}
                </h3>
                <p className="text-[var(--color-text-secondary)] text-sm mb-6 leading-relaxed">
                  {questions.length} questions · {timerMode === 'per_question' ? `${formatCountdown(timerSeconds)} per question` : `${formatCountdown(timerSeconds)} for the round`}
                </p>
                {play.campaign.sponsorTagline && (
                  <p className="text-sm text-[var(--color-accent)] mb-6">{play.campaign.sponsorTagline}</p>
                )}
                <button
                  type="button"
                  onClick={startQuiz}
                  className="bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-semibold py-3 px-6 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)]"
                >
                  Start Quiz
                </button>
              </>
            )}

            {!loading && play?.state === 'completed' && play.attempt && !shouldShowSurvey && (
              <div className="space-y-6">
                <div className="w-16 h-16 bg-[var(--color-success-subtle)] rounded-[var(--radius-lg)] flex items-center justify-center mx-auto text-[var(--color-success)]">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">Round Complete</h3>
                <div className="text-3xl font-bold text-[var(--color-accent)] tabular-nums">
                  {play.attempt.totalPoints} / {play.attempt.maxPoints} pts
                </div>
                <p className="text-[var(--color-text-secondary)] text-sm">
                  Completed in {formatCountdown(play.attempt.durationSeconds)}. Score added to the campaign leaderboard.
                  You cannot rejoin this round.
                </p>
              </div>
            )}

            {!loading && play?.attempt && shouldShowSurvey && (
              <div className="space-y-6 text-left">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-[var(--color-success-subtle)] rounded-[var(--radius-lg)] flex items-center justify-center mx-auto text-[var(--color-success)]">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-[var(--color-text-primary)]">Round Submitted</h3>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Your quiz answers are already locked in. This quick survey does not affect your score or timing.
                  </p>
                </div>
                <div className="border border-[var(--color-border)] rounded-[var(--radius-lg)] p-5 space-y-5">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Optional survey</p>
                    <h4 className="text-lg font-semibold text-[var(--color-text-primary)]">{surveyQuestion?.question}</h4>
                  </div>
                  <QuizQuestionMediaView media={surveyQuestion?.media} />
                  <div className="grid grid-cols-1 gap-4">
                    {(surveyQuestion?.options ?? []).map((option, idx) => (
                      <button
                        type="button"
                        key={`survey-${idx}`}
                        onClick={() => void submitSurvey(idx)}
                        className="p-4 text-left rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)] transition-colors duration-[var(--duration-fast)] font-medium text-sm"
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-7 h-7 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] flex items-center justify-center text-xs font-medium text-[var(--color-text-tertiary)]">
                            {String.fromCharCode(65 + idx)}
                          </span>
                          {option}
                        </div>
                      </button>
                    ))}
                  </div>
                  {surveyError && <p className="text-sm text-[var(--color-error)]">{surveyError}</p>}
                </div>
              </div>
            )}

            {isQuizActive && play?.session && (
              <div className="text-left space-y-8 animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center mb-8 gap-4">
                  <span className="text-xs font-medium text-[var(--color-accent)]">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-xs font-semibold tabular-nums text-[var(--color-text-primary)]">
                    {secondsLeft !== null ? formatCountdown(secondsLeft) : '--:--'}
                  </span>
                </div>
                <p className="text-xs text-[var(--color-text-tertiary)] -mt-5">
                  {timerMode === 'per_question' ? 'Timer resets on every question. Unanswered questions auto-skip when time runs out.' : 'One timer applies to the full round.'}
                </p>

                <div className="w-full h-[3px] bg-[var(--color-surface-2)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-accent)] transition-all duration-300 rounded-full"
                    style={{
                      width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                    }}
                  />
                </div>

                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2 leading-tight">
                  {questions[displayIndex]?.question}
                </h3>
                <QuizQuestionMediaView media={questions[displayIndex]?.media} />
                <p className="text-xs text-[var(--color-text-tertiary)] mb-6">
                  Worth {questions[displayIndex]?.points ?? 1} point(s)
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {currentOptionOrder.map((origOptionIndex, idx) => (
                    <button
                      type="button"
                      key={`${displayIndex}-${origOptionIndex}`}
                      onClick={() => handleAnswerSelect(origOptionIndex)}
                      className={cn(
                        'p-4 text-left rounded-[var(--radius-md)] border transition-colors duration-[var(--duration-fast)] font-medium text-sm',
                        answers[displayIndex] === origOptionIndex
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-text-primary)]'
                          : 'border-[var(--color-border)] bg-[var(--color-surface-1)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)]',
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-7 h-7 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] flex items-center justify-center text-xs font-medium text-[var(--color-text-tertiary)]">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        {questions[displayIndex]?.options[origOptionIndex]}
                      </div>
                    </button>
                  ))}
                </div>

                {(quizFinished || secondsLeft === 0) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pt-10 flex flex-col items-center gap-3"
                  >
                    {submitError && (
                      <>
                        <p className="text-sm text-[var(--color-error)]">{submitError}</p>
                        <button
                          type="button"
                          onClick={() => void submitQuiz()}
                          disabled={isSubmittingAttempt}
                          className="bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-semibold py-2 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] disabled:opacity-60"
                        >
                          {isSubmittingAttempt ? 'Retrying…' : 'Try Again'}
                        </button>
                      </>
                    )}
                    {!submitError && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {isSubmittingAttempt ? 'Submitting your round…' : 'Wrapping up your round…'}
                      </p>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
